import { sanitizePlayerName } from './SaveSystem';

export interface LeaderboardEntry {
  playerId: string;
  name: string;
  score: number;
  level: number;
  stars: number;
  updatedAt: string;
}

export interface LeaderboardResult {
  entries: LeaderboardEntry[];
  shared: boolean;
}

const LOCAL_KEY = 'mouthbound-ruins-leaderboard-cache-v1';

export class LeaderboardSystem {
  constructor(private readonly playerId: string) {}

  async list(): Promise<LeaderboardResult> {
    try {
      const response = await fetch('/api/leaderboard', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Leaderboard responded ${response.status}`);
      const payload = await response.json() as { entries?: unknown };
      const entries = sanitizeEntries(payload.entries);
      this.storeLocal(entries);
      return { entries, shared: true };
    } catch {
      return { entries: this.readLocal(), shared: false };
    }
  }

  async submit(name: string, score: number, level: number, stars: number): Promise<void> {
    const entry: LeaderboardEntry = {
      playerId: this.playerId,
      name: sanitizePlayerName(name),
      score: Math.max(0, Math.floor(score)),
      level: Math.max(1, Math.floor(level)),
      stars: Math.max(0, Math.floor(stars)),
      updatedAt: new Date().toISOString(),
    };
    this.upsertLocal(entry);
    try {
      const response = await fetch('/api/leaderboard', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(entry),
      });
      if (!response.ok) throw new Error(`Leaderboard responded ${response.status}`);
      const payload = await response.json() as { entries?: unknown };
      this.storeLocal(sanitizeEntries(payload.entries));
    } catch { /* The local score remains visible until the server is reachable. */ }
  }

  private upsertLocal(entry: LeaderboardEntry): void {
    const entries = this.readLocal();
    const previous = entries.find((item) => item.playerId === entry.playerId);
    const merged = previous && previous.score > entry.score ? { ...previous, name: entry.name } : entry;
    this.storeLocal([...entries.filter((item) => item.playerId !== entry.playerId), merged]);
  }

  private readLocal(): LeaderboardEntry[] {
    try { return sanitizeEntries(JSON.parse(localStorage.getItem(LOCAL_KEY) ?? '[]')); }
    catch { return []; }
  }

  private storeLocal(entries: LeaderboardEntry[]): void {
    const sorted = sortEntries(entries).slice(0, 20);
    try { localStorage.setItem(LOCAL_KEY, JSON.stringify(sorted)); } catch { /* private mode */ }
  }
}

export function sanitizeEntries(value: unknown): LeaderboardEntry[] {
  if (!Array.isArray(value)) return [];
  return sortEntries(value.flatMap((raw) => {
    if (!raw || typeof raw !== 'object') return [];
    const item = raw as Partial<LeaderboardEntry>;
    const playerId = String(item.playerId ?? '');
    const score = Math.max(0, Math.floor(Number(item.score) || 0));
    const level = Math.max(1, Math.min(50, Math.floor(Number(item.level) || 1)));
    const stars = Math.max(0, Math.min(level * 3, Math.floor(Number(item.stars) || 0)));
    if (!/^[a-zA-Z0-9-]{16,64}$/.test(playerId)) return [];
    return [{ playerId, name: sanitizePlayerName(item.name), score, level, stars, updatedAt: String(item.updatedAt ?? '') }];
  })).slice(0, 20);
}

function sortEntries(entries: LeaderboardEntry[]): LeaderboardEntry[] {
  return [...entries].sort((a, b) => b.score - a.score || b.level - a.level || a.name.localeCompare(b.name));
}
